<template>
  <div class="paf-search">
    <div class="form-group">
      <label :for="$id('search-value')">
        Enter your post code to search for your address:
      </label>

      <b-form-input :id="$id('search-value')"
                    v-model="searchText"
                    type="text"
                    placeholder="Enter a post code"
                    aria-label="Enter a post code"
                    @input="onSearchValueChange">
      </b-form-input>
    </div>
    <div class="form-group">
      <label :for="$id('search-results')">
        Select your address:
      </label>

      <b-form-select :id="$id('search-results')"
                     v-model="selectedPostKey"
                     :disabled="!hasResults"
                     @change="onAddressSelected">
        <b-form-select-option value="0"
                              selected>
          - Please select an address -
        </b-form-select-option>
        <b-form-select-option v-for="(address, index) in addresses"
                              :key="index"
                              :value="address.postKey">
          {{ address.address }}
        </b-form-select-option>
      </b-form-select>

      <span v-if="showValidation && selectedPostKey ==='0'"
            class="validation-error">
        Please select an address.
      </span>
    </div>
  </div>
</template>

<script>
import { debounce, isEqual } from "lodash";
import { pafAddressSearch, pafAddressLoad } from "~/Scripts/resources/addressSearchResource";
import AddressSearchMixin from "~/Scripts/mixins/AddressSearchMixin";

export default {

  mixins: [AddressSearchMixin],

  data () {
    return {
      searchText: null,
      selectedPostKey: "0",
      addresses: [],
      debouncedSearch: null
    };
  },

  computed: {
    /**
     * Whether or not we have results
     */
    hasResults () {
      return this.addresses && this.addresses.length > 0;
    }
  },

  watch: {
    /**
     * When the address linked to this component is changed, clear the selected address
     */
    value: {
      deep: true,
      handler ( val ) {
        if ( !isEqual( val, this.address ) ) {
          this.selectedPostKey = "0";
          this.address = this.blankAddress();
        }
      }
    }
  },

  methods: {
    /**
     * As data is entered into the search field, "debounce" it so that we do
     * only search after no key presses for x ms
     */
    onSearchValueChange () {
      if ( this.searchText.length >= 0 ) {
        if ( typeof this.debouncedSearch !== "function" ) {
          this.debouncedSearch = debounce( this.runSearch, 500 );
        }
        this.debouncedSearch();
      }
    },

    /**
     * Run a search and populate the results drop down
     */
    runSearch () {
      this.addresses = [];

      pafAddressSearch( this.searchText )
        .then( ( result ) => {
          this.addresses = result.data || [];
        } )
        .catch( () => {
          this.addresses = [];
        } );
    },

    /**
     * An address has been selected so load the full address details
     */
    onAddressSelected () {
      if ( this.hasResults && this.selectedPostKey ) {
        pafAddressLoad( this.selectedPostKey )
          .then( ( result ) => {
            this.updateAddress( result.data );
          } )
          .catch( () => {
            this.updateAddress( null );
          } );
      }
    },

    /**
     * Update the "address" model with the "PAF Full Address" model the user selected
     */
    updateAddress ( pafFullAddress ) {
      if ( pafFullAddress ) {
        // Convert the selected org to our common address model
        this.address = {
          id: null,
          orgName: pafFullAddress.organisation,
          address1: pafFullAddress.property,
          address2: pafFullAddress.street,
          address3: pafFullAddress.locality,
          town: pafFullAddress.town,
          region: pafFullAddress.county,
          postcode: pafFullAddress.postcode,
          countryId: this.getCountryId( pafFullAddress.nhsRegion ),
          country: null
        };
      } else {
        this.address = this.blankAddress();
      }

      this.$emit( "input", this.address );
    },

    /**
     * The most reliable clue as to where the address is country wise, comes from the NHS Region.
     * We have Wales, Scotland, Northern Ireland, anything else is England
     *
     * @param {string} nhsRegion The NHS Region
     */
    getCountryId ( nhsRegion ) {
      let countryId = null;
      const countryName = nhsRegion || "England";

      switch ( countryName.toLowerCase() ) {
        case "wales": {
          countryId = 2;
          break;
        }
        case "scotland": {
          countryId = 3;
          break;
        }
        case "northern ireland": {
          countryId = 4;
          break;
        }
        default: {
          countryId = 1;
          break;
        }
      }

      return countryId;
    }
  }
};
</script>
